import React, { useEffect, useState, useRef } from 'react';
import { FiGrid } from 'react-icons/fi';
import { useSelector, useDispatch } from 'react-redux';

import GridView from './components/GridView';
import { ReactComponent as ProcessIcon } from 'assets/process.svg';

import useStyles from './styles';
import { PageHeader } from 'components';
import { accountsSlice } from 'app/store';
import { DataNotFound } from 'components/DataNotFound';
import AssetsActionBar from './components/AssetsActionBar';
const { selectUserSetting, selectActiveUser } = accountsSlice;
import { getAssetTypes } from 'features/assets/assetManagerSlice';
import { CardContainer } from './components/CardView/CardContainer';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import {
    fetchAllCategories,
    getAllCategories,
    getCategoryStatus,
    resetCategoryStatus,
} from 'features/category/categorySlice';
import {
    selectAssetPage,
    fetchAssetsPage,
    selectAssetPageInfo,
    selectAssetsLoadingStatus,
    setAssetsStatus,
    resetAssetsTableState,
} from 'features/assets/assetsTableSlice';
import {} from 'helpers';
import { AssetTypes } from 'GeminiViewerComponent/helpers';

function Assets({ assetType }) {
    //#region Constants
    const assetTypeId = assetType === 'procedures' ? AssetTypes.Procedure : `${AssetTypes.Product}|${AssetTypes.Place}`;
    const INITIAL_PAYLOAD = {
        page: 0,
        pageSize: 50,
        sort: `-is_pinned,display_name`,
        searchByAssetType: assetTypeId,
    };
    //#endregion Constants

    //#region Hooks
    const dispatch = useDispatch();
    //#endregion Hooks

    //#region State
    const [sortBy, setSortBy] = useState('display_name');
    const [inputValue, setInputValue] = useState('');
    const [sortDirection, setSortDirection] = useState('ASC');
    const [isLoadingAssets, setLoadingAssets] = useState(false);
    const [assetPayload, setAssetPayload] = useState(INITIAL_PAYLOAD);
    const [selectedAssetCategory, setSelectedAssetCategory] = useState([]);
    //#endregion State

    //#region Selectors
    const theme = useSelector(selectDefaultTheme);
    const classes = useStyles(theme);

    const assetViewMode = useSelector((state) =>
        selectUserSetting(state, `${assetType === 'assets' ? 'asset_view_mode' : 'procedure_view_mode'}`)
    );
    const categoryView = useSelector((state) =>
        selectUserSetting(state, `${assetType === 'assets' ? 'asset_category_view' : 'procedure_category_view'}`)
    );
    const assets = useSelector(selectAssetPage);
    const pageInfo = useSelector(selectAssetPageInfo);
    const assetsStatus = useSelector(selectAssetsLoadingStatus);
    const activeUser = useSelector(selectActiveUser);
    const categoryStatus = useSelector(getCategoryStatus);
    const allCategories = useSelector(getAllCategories);
    const readOnlyMode = activeUser?.role === 'User' ? true : false;
    //#endregion Selectors

    //#region Refs
    const isComponentLoaded = useRef(true);
    //#endregion Refs

    //#region Effects
    useEffect(() => {
        dispatch(setAssetsStatus(LoadingStatus.Idle));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSelectedAssetCategory([]);
    }, [assetType]);

    const getAssets = async () => {
        if (assetsStatus === LoadingStatus.Idle) {
            await dispatch(activateLoading());
            setLoadingAssets(true);
            await dispatch(fetchAssetsPage(assetPayload));
            await dispatch(getAssetTypes());
            await dispatch(deactivateLoading());
        }
        if (assetsStatus === LoadingStatus.Loaded || assetsStatus === LoadingStatus.Failed) {
            setLoadingAssets(false);
        }
    };

    useEffect(() => {
        getAssets();
        const fetchCategories = async () => {
            await dispatch(activateLoading());
            await dispatch(
                fetchAllCategories({
                    asset_type_id: assetType === 'assets' ? AssetTypes.Product : AssetTypes.Procedure,
                })
            );
            await dispatch(deactivateLoading());
        };
        if (categoryStatus === LoadingStatus.Idle) {
            fetchCategories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetsStatus, categoryStatus]);

    // it's called only when assetPayload changes not on component load.
    useEffect(() => {
        const getAssetByFilter = async () => {
            await dispatch(activateLoading());
            if (isComponentLoaded.current) {
                isComponentLoaded.current = false;
                return;
            }
            await dispatch(fetchAssetsPage({ ...assetPayload }));
            await dispatch(deactivateLoading());
        };
        getAssetByFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetPayload]);

    useEffect(() => {
        return () => {
            if (isComponentLoaded.current === false) {
                setInputValue('');
                setSortBy('');
                setSortDirection('');
                setAssetPayload({
                    ...INITIAL_PAYLOAD,
                    searchByAssetType: assetType === 'procedures' ? '1|2' : '3', //Add another IDs if generated other than this
                });
                dispatch(resetAssetsTableState());
                dispatch(resetCategoryStatus());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetType]);

    // map view modes to preferred page sizes (counts)
    const VIEW_MODE_PAGE_SIZES = {
        list: 50, // existing default
        card: 14, // card view uses smaller chunk; matches CardView minimal chunk logic
    };

    // When view mode changes, recompute assetPayload.page (offset) so logical position is preserved.
    useEffect(() => {
        const newPageSize = VIEW_MODE_PAGE_SIZES[assetViewMode || 'card'] ?? VIEW_MODE_PAGE_SIZES.list;
        setAssetPayload((prev) => {
            if (!prev) return prev;
            const oldOffset = Number(prev.page) || 0; // prev.page is offset
            const oldPageSize = Number(prev.pageSize) || VIEW_MODE_PAGE_SIZES.list;
            if (oldPageSize === newPageSize && prev.pageSize === newPageSize) {
                return prev;
            }

            const newStartIndex = Math.floor(oldOffset / newPageSize) * newPageSize;
            return {
                ...prev,
                page: newStartIndex,
                pageSize: newPageSize,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetViewMode]);
    //#endregion Effects

    //#region Methods
    const setSorting = (_sortBy, _sortDirection) => {
        setSortBy(_sortBy);
        setSortDirection(_sortDirection);
        if (_sortBy !== '') {
            if (_sortBy === 'last_published_date') {
                setAssetPayload({
                    ...assetPayload,
                    sort: `-is_pinned,${_sortDirection === 'ASC' ? '' : '-'}${_sortBy}`,
                });
            } else {
                setAssetPayload({
                    ...assetPayload,
                    sort: `-is_pinned,${_sortDirection === 'DESC' ? '-' : ''}${_sortBy}`,
                });
            }
        }
    };
    //#endregion Methods

    //#region Render time calcs
    //#endregion Render time calcs

    //#region Render
    return (
        <div style={{ height: '100vh', width: 'calc(100% - 165px)' }} className={classes.content}>
            <PageHeader
                header={assetType === 'procedures' ? 'Procedures' : 'Assets'}
                icon={assetType === 'procedures' ? <ProcessIcon /> : <FiGrid />}
            />
            <AssetsActionBar
                assetType={assetType}
                assetPayload={assetPayload}
                setAssetPayload={setAssetPayload}
                setInputValue={setInputValue}
                setSorting={setSorting}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
            />

            {assets.length === 0 &&
            assetsStatus === LoadingStatus.Loaded &&
            categoryStatus === LoadingStatus.Loaded &&
            (!categoryView || allCategories?.length === 0) ? (
                <DataNotFound
                    title={assetType === 'procedures' ? 'Procedures' : 'Assets'}
                    inputLength={inputValue.length}
                />
            ) : assetViewMode === 'list' ? (
                <GridView
                    assets={assets}
                    pageInfo={pageInfo}
                    searchString={inputValue}
                    sortDirection={sortDirection}
                    sortBy={sortBy}
                    loadingStatus={isLoadingAssets ? '' : 'No Assets'}
                    handleSort={(_sortBy, _sortDirection) => setSorting(_sortBy, _sortDirection)}
                    readOnly={readOnlyMode}
                    assetType={assetType}
                    assetPayload={assetPayload}
                    categoryView={categoryView}
                    selectedAssetCategory={selectedAssetCategory}
                    setSelectedAssetCategory={setSelectedAssetCategory}
                />
            ) : (
                <CardContainer
                    assets={assets}
                    pageInfo={pageInfo}
                    searchString={inputValue}
                    isLoadingAssets={isLoadingAssets}
                    readOnly={readOnlyMode}
                    assetType={assetType}
                    assetPayload={assetPayload}
                    setAssetPayload={setAssetPayload}
                    categoryView={categoryView}
                    selectedAssetCategory={selectedAssetCategory}
                    setSelectedAssetCategory={setSelectedAssetCategory}
                />
            )}
        </div>
    );
    //#endregion Render
}

export { Assets };
